import { lazy, Suspense, useEffect, useRef } from "react";
import { Route, Routes, useLocation, Link, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Header from "./components/Header.jsx";
import Footer from "./components/Footer.jsx";
import AnalyticsConsent from "./components/AnalyticsConsent.jsx";
import { startAnalytics, trackPageView } from "./lib/analytics.js";
import Home from "./pages/Home.jsx";
import { newsArticles } from "./data/legacyContent.js";
import Privacy from "./pages/Privacy.jsx";
import { appPolicies } from "./data/appPolicyIndex.generated.js";
import { appSupportRoutes } from "./data/appSupportRoutes.js";
import { legacyRoutes } from "./data/legacyRoutes.js";
import Sitemap from "./pages/Sitemap.jsx";
import { applyPageSEO } from "./data/seo.js";
const AppPolicy = lazy(() => import("./pages/AppPolicy.jsx"));
const AppSupport = lazy(() => import("./pages/AppSupport.jsx"));
const HowWeWork = lazy(() => import("./pages/HowWeWork.jsx"));
const Software = lazy(() => import("./pages/Software.jsx"));
const AIAutomation = lazy(() => import("./pages/AIAutomation.jsx"));
const Apps = lazy(() => import("./pages/Apps.jsx"));
const News = lazy(() => import("./pages/News.jsx"));
const NewsArticle = lazy(() => import("./pages/NewsArticle.jsx"));
const About = lazy(() => import("./pages/About.jsx"));
const Contact = lazy(() => import("./pages/Contact.jsx"));
const AnalyticsDashboard = lazy(() => import("./components/AnalyticsDashboard.jsx"));
function RouteEffects() {
  const {
    pathname,
    hash,
    state
  } = useLocation();
  const previousRoute = useRef(null);
  const preserveSolutionScroll = Boolean(state?.preserveSolutionScroll);
  useEffect(() => {
    const title = applyPageSEO(pathname);
    trackPageView(pathname, title);
    const previous = previousRoute.current;
    previousRoute.current = {
      pathname,
      hash
    };
    // Changing a solution updates its content in place; global navigation still resets normally.
    if (preserveSolutionScroll && previous?.pathname.startsWith("/solutions") && pathname.startsWith("/solutions") && !hash && !previous.hash) return;
    let timer;
    let attempts = 0;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const settleNavigation = () => {
      const target = document.getElementById(hash ? decodeURIComponent(hash.slice(1)) : "main");
      if (!target && attempts++ < 20) {
        timer = window.setTimeout(settleNavigation, 50);
        return;
      }
      if (target) {
        target.setAttribute("tabindex", "-1");
        target.focus({
          preventScroll: true
        });
      }
      if (hash) target?.scrollIntoView({
        behavior: reducedMotion ? "instant" : "smooth",
        block: "start"
      });else window.scrollTo({
        top: 0,
        behavior: "instant"
      });
    };
    timer = window.setTimeout(settleNavigation, 80);
    return () => window.clearTimeout(timer);
  }, [pathname, hash, preserveSolutionScroll]);
  return null;
}
function NotFound() {
  return <main id="main" className="kw-simple-page kw-not-found">
      <span className="kw-eyebrow">404 / A SMALL DETOUR</span>
      <h1>
        Let’s get you
        <br />
        back on track.
      </h1>
      <p>This page may have moved with the new site.</p>
      <Link className="kw-button kw-button-red" to="/">
        Back to the homepage <ArrowUpRight size={18} />
      </Link>
    </main>;
}
export default function App() {
  const {
    pathname
  } = useLocation();
  const isAdmin = /^\/(admin|login)(\/|$)/.test(pathname);
  useEffect(() => {
    const cleanup = startAnalytics();
    return typeof cleanup === "function" ? cleanup : undefined;
  }, []);
  return <div className={isAdmin ? "kw-admin-root" : "kw-site"}>
      <RouteEffects />
      {!isAdmin && <>
          <a href="#main" className="kw-skip-link">
            Skip to content
          </a>
          <Header />
        </>}
      <Suspense fallback={<main className="kw-page-loader" aria-live="polite">
            <span className="kw-loader-mark" />
            <span>Opening the next chapter…</span>
          </main>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/software" element={<Software />} />
          <Route path="/apps" element={<Apps />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<NewsArticle />} />
          <Route path="/blog" element={<Navigate to="/news" replace />} />
          {newsArticles.map(article => <Route key={article.originalPath} path={article.originalPath} element={<Navigate to={`/news/${article.slug}`} replace />} />)}
          <Route path="/ai-automation" element={<AIAutomation />} />
          <Route path="/solutions/ai-solutions" element={<Navigate to="/ai-automation" replace />} />
          <Route path="/solutions/*" element={<Navigate to="/software" replace />} />
          <Route path="/work/*" element={<Navigate to="/software" replace />} />
          <Route path="/codes" element={<Navigate to="/software" replace />} />
          <Route path="/work/codes" element={<Navigate to="/software" replace />} />
          <Route path="/tiaa" element={<Navigate to="/apps#tiaa" replace />} />
          <Route path="/how-we-work" element={<HowWeWork />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/app-support" element={<AppSupport />} />
          {appSupportRoutes.map(route => <Route key={route.path} path={route.path} element={<AppSupport app={route.app} />} />)}
          {legacyRoutes.map(route => <Route key={route.source} path={route.source} element={<Navigate to={route.destination} replace />} />)}
          {appPolicies.map(policy => <Route key={policy.slug} path={`/${policy.slug}`} element={<AppPolicy slug={policy.slug} />} />)}
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/login" element={<AnalyticsDashboard />} />
          <Route path="/admin/analytics" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {!isAdmin && <>
          <Footer />
          <AnalyticsConsent />
        </>}
    </div>;
}
